import { useSuspenseQueries } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getIntegrationInstallationKey } from '@gitbook/app-database';

import { apiClient, apiQuery } from '../../state';
import { IntegrationConfigureGenericFlow } from './IntegrationConfigureGenericFlow';
import { assertIsExtendedSpaceInstallation } from './integrations.utils';

export interface IntegrationConfigureSpaceFlowProps {
    /** Integration to configure */
    integration: api.Integration;
    /** Installation of the integration in the space */
    spaceInstallation: api.IntegrationSpaceInstallation;
    /** Callback when the configuration is finished */
    onComplete?: () => void;
}

/**
 * Component to render the configuration flow for an integration in a space.
 */
export function IntegrationConfigureSpaceFlowById(props: {
    integrationName: string;
    organizationId: string;
    spaceId: string;
    onComplete?: () => void;
}) {
    const { integrationName, organizationId, spaceId, onComplete } = props;

    const [{ data: integration }, { data: spaceInstallation }] = useSuspenseQueries({
        queries: [
            apiQuery('getIntegrationByName', { integrationName }),
            apiQuery('getIntegrationSpaceInstallation', {
                installationId: getIntegrationInstallationKey(integrationName, {
                    organization: organizationId,
                }),
                spaceId,
                integrationName,
                extended: true,
            }),
        ] as const,
    });

    return (
        <IntegrationConfigureSpaceFlow
            integration={integration}
            spaceInstallation={spaceInstallation}
            onComplete={onComplete}
        />
    );
}

/**
 * Component to render the configuration flow for an integration in a space.
 * The `spaceInstallation` should be live-updated with the configuration changes, consider using <IntegrationConfigureSpaceFlowById />.
 */
export function IntegrationConfigureSpaceFlow(
    props: IntegrationConfigureSpaceFlowProps
): React.ReactElement {
    const { integration, spaceInstallation, onComplete } = props;
    assertIsExtendedSpaceInstallation(spaceInstallation);

    const configuration = integration.configurations?.space;
    if (!configuration) {
        throw new Error(`Expected a space configuration for integration ${integration.name}`);
    }

    return (
        <IntegrationConfigureGenericFlow
            integration={integration}
            onComplete={() => {
                analytics.logEvent(
                    analytics.createSpaceEvent(
                        {
                            key: spaceInstallation.space.id,
                        },
                        {
                            type: 'integration_configuration_complete',
                            integration: integration.name,
                        }
                    )
                );

                onComplete?.();
            }}
            // We need the useMemo to ensure ContentKit doesn't re-render on every parent render
            {...React.useMemo(
                () => ({
                    configuration,
                    contentKitProps: {
                        spaceInstallation: {
                            configuration: spaceInstallation.configuration,
                        },
                    },
                    contentKitContext: {
                        type: 'configuration_space',
                        spaceId: spaceInstallation.space.id,
                        editable: true,
                    },
                    formValue: spaceInstallation.configuration,
                    formBaseUrl: spaceInstallation.urls.publicEndpoint,
                    formOnChangeValue: async (value) => {
                        await apiClient.integrations.updateIntegrationSpaceInstallation(
                            integration.name,
                            spaceInstallation.installation,
                            spaceInstallation.space.id,
                            { configuration: value },
                            { extended: true }
                        );
                    },
                }),
                [configuration, spaceInstallation.configuration]
            )}
        />
    );
}
